import { apiClient } from "../client";
import type { Envelope, LaravelPaginator, RatingDistribution } from "./catalog";

/**
 * Review endpoints (verified against the Postman collection's "Ecommerce —
 * Reviews" folder). The same `/reviews` resource is shared across
 * reviewable types (product|course|astrologer) via `reviewable_type` +
 * `reviewable_id` — this file only exercises the `astrologer` type, which is
 * what the astrology app needs.
 *
 * NOTE: the collection exposes List, Summary and Submit (create) only —
 * there is no documented Update/Delete/single-Review-by-id/"my reviews"
 * endpoint. Do not guess those routes; callers should design around
 * create + list + summary only.
 */

/** A review's author, as embedded on the review record. Field names aren't
 * pinned down by the Postman doc, so every likely alias is tolerated. */
export interface ReviewAuthor {
  id?: number | string;
  name?: string;
  first_name?: string;
  last_name?: string;
  avatar?: string | null;
  avatar_url?: string | null;
  image?: string | null;
  [key: string]: unknown;
}

export interface Review {
  id: number | string;
  reviewable_type?: string;
  reviewable_id?: number | string;
  rating: number;
  title?: string | null;
  comment?: string | null;
  /** Some deployments may key the body under `review`/`text`/`body` instead. */
  review?: string | null;
  text?: string | null;
  body?: string | null;
  user?: ReviewAuthor | null;
  reviewer?: ReviewAuthor | null;
  user_id?: number | string;
  is_verified?: boolean;
  verified?: boolean;
  verified_purchase?: boolean;
  helpful_count?: number;
  helpful?: number;
  images?: Array<{ id?: number | string; url?: string; original_url?: string } | string>;
  media?: Array<{ id?: number | string; url?: string; original_url?: string } | string>;
  created_at?: string;
  date?: string;
  [key: string]: unknown;
}

export type ReviewableType = "astrologer" | "product" | "course";

export interface ReviewsListParams {
  reviewable_type: ReviewableType;
  reviewable_id: number | string;
  sort_by?: "recent" | "helpful" | "rating_high" | "rating_low";
  rating?: number;
  verified_only?: boolean;
  per_page?: number;
  page?: number;
}

export type ReviewsListResponse =
  | Envelope<LaravelPaginator<Review> | Review[]>
  | LaravelPaginator<Review>
  | Review[];

export interface ReviewsSummaryParams {
  reviewable_type: ReviewableType;
  reviewable_id: number | string;
}

/** GET /reviews/summary payload — total + average are documented; a
 * per-star breakdown isn't, but is tolerated if a deployment adds one. */
export interface ReviewSummary {
  total_reviews: number;
  average_rating: number;
  rating_breakdown?: RatingDistribution;
  distribution?: RatingDistribution;
  breakdown?: RatingDistribution;
  [key: string]: unknown;
}

export type ReviewSummaryResponse = Envelope<ReviewSummary> | ReviewSummary;

export interface SubmitReviewPayload {
  reviewable_type: ReviewableType;
  reviewable_id: number | string;
  rating: number;
  /** 20–1000 chars per the upstream Validator. */
  comment: string;
  /** Max 255 chars. */
  title?: string;
}

export type SubmitReviewResponse = Envelope<Review> | Review;

export const reviews = {
  /** GET /reviews — paginated, filterable/sortable list for one reviewable. */
  list: (params: ReviewsListParams) =>
    apiClient.get<ReviewsListResponse>("/reviews", { params: { ...params } }),

  /** GET /reviews/summary — aggregate rating for one reviewable. Sanctum-guarded
   * upstream despite being read-only, so this rides the BFF session like
   * everything else. */
  summary: (params: ReviewsSummaryParams) =>
    apiClient.get<ReviewSummaryResponse>("/reviews/summary", { params: { ...params } }),

  /** POST /reviews — the reviewer must have actually booked/purchased/enrolled
   * the item and not already reviewed it; the upstream enforces both and
   * returns 422 otherwise. */
  create: (payload: SubmitReviewPayload) =>
    apiClient.post<SubmitReviewResponse>("/reviews", payload),
};
